import React, { useState, useMemo, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import Tabs from '@/components/Tabs'
import { AuthType, type Item } from '@/components/Tabs/index.type'
import CustomTextInput from '@/components/CustomTextInput'
// import OtherLoginItems from '@/components/OtherLoginItems' // 暂时未使用
import CustomButton from '@/components/CustomButton'
import { APIWeb } from '@/api'
import { Toast } from 'react-vant'
import { useTransactionRefresh } from '@/hooks'
import { useTranslation } from 'react-i18next'
import { Checkbox } from 'react-vant';
export default React.memo(function Login() {

  const { refreshAfterTransaction } = useTransactionRefresh()
  const { t } = useTranslation()

  const navigate = useNavigate()
  const [selectedType, setSelectedType] = useState<AuthType>()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isAgreed, setIsAgreed] = useState(false)
  const [errors, setErrors] = useState<{ email?: string, password?: string }>({})
  const [loading, setLoading] = useState(false)

  // 登录类型选项
  const loginTypeItems = useMemo<Item<AuthType>[]>(() => [
    { type: AuthType.Email, text: t('邮箱') },
    { type: AuthType.Phone, text: t('手机号') }
  ], [t])

  // 邮箱校验函数 - 参考注册页面
  const validateEmail = useCallback((emailValue: string) => {
    if (!emailValue) {
      return t('请输入邮箱地址')
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(emailValue)) {
      return t('请输入有效的邮箱地址')
    }
    return ''
  }, [t])

  // 密码校验函数 - 参考设置密码页面
  const validatePassword = useCallback((pwd: string) => {
    if (!pwd) {
      return t('请输入密码')
    }
    if (pwd.length < 8) {
      return t('密码长度不能少于8个字符')
    }
    if (!/(?=.*[a-z])(?=.*[A-Z])/.test(pwd)) {
      return t('密码必须包含大小写字母')
    }
    return ''
  }, [t])

  // 邮箱输入处理
  const handleEmailChange = useCallback((value: string) => {
    setEmail(value)
    const error = validateEmail(value)
    setErrors(prev => ({ ...prev, email: error }))
  }, [validateEmail])

  // 密码输入处理
  const handlePasswordChange = useCallback((value: string) => {
    setPassword(value)
    const error = validatePassword(value)
    setErrors(prev => ({ ...prev, password: error }))
  }, [validatePassword])


  // 检查表单是否有效
  const isFormValid = useMemo(() => {
    if (selectedType === AuthType.Email) {
      return email && password && !validateEmail(email) && !validatePassword(password)
    }
    return false // 手机登录暂未实现
  }, [selectedType, email, password, validateEmail, validatePassword])

  // 登录
  const handleLogin = useCallback(async () => {
    if (!isFormValid || !isAgreed) return

    setLoading(true)
    setErrors({ email: '', password: '' })

    try {
      const data = await APIWeb.login({
        account: email,
        password,
      })
      localStorage.setItem('token', data.token || '')
      await refreshAfterTransaction()

      Toast.success({
        message: t('登录成功'),
        forbidClick: true,
        onClose: () => navigate('/home')
      })
    } catch (error) {
      setPassword('')
      console.error('登录失败', error)
    } finally {
      setLoading(false)
    }
  }, [email, password, isFormValid, isAgreed, navigate])

  const onChange = useCallback((type: AuthType) => {
    if (type === AuthType.Phone) {
      Toast.info({
        message: t('当前地区暂不支持手机号登录注册'),
        position: 'middle'
      })
      return
    }
    setSelectedType(type)
  }, [t])




  const EmailLoginView = useMemo(
    () => (
      <div className="space-y-5">
        <div className="space-y-2">
          <div className="text-sm text-gray-700 mb-1">{t('您的电子邮箱')}</div>
          <CustomTextInput
            value={email}
            onChangeText={handleEmailChange}
            placeholder={t('请输入您的电子邮箱')}
            isErrored={Boolean(errors.email)}
            inputMode='email'
          />
          {errors.email && (
            <p className="mt-1 text-xs text-red-600">{errors.email}</p>
          )}
        </div>
        <div className="space-y-2">
          <div className="text-sm text-gray-700 mb-1">{t('密码')}</div>
          <CustomTextInput
            isErrored={Boolean(errors.password)}
            value={password}
            onChangeText={handlePasswordChange}
            placeholder={t('请输入您的密码')}
            secureTextEntry={true}
            isShowSmallEyes={true}
          />
          {errors.password && (
            <p className="mt-1 text-xs text-red-600">{errors.password}</p>
          )}
        </div>
      </div>
    ),
    [email, password, handleEmailChange, handlePasswordChange, errors.email, errors.password, t]
  )

  const PhoneLoginView = useMemo(
    () => (
      <div className="text-5xl text-gray-200 text-center my-20">
        {t('即将上线')}
      </div>
    ),
    [t]
  )

  const LoginViewRender = useMemo(
    () => (
      selectedType === AuthType.Email ? EmailLoginView : PhoneLoginView
    ),
    [selectedType, EmailLoginView, PhoneLoginView]
  )



  const handleAgreementToggle = useCallback(() => {
    setIsAgreed(prev => !prev)
  }, [])

  return (
    <div className="bg-white h-full">
      <div className="overflow-y-auto">
        <div>
          {/* Logo */}
          <div className="w-full pt-14 pb-8 flex items-center justify-center">
            <img className="w-34" src="/icon/logo.svg" alt="Logo" />
          </div>

          <div className="px-6">
            <Tabs<AuthType> items={loginTypeItems} defaultSelected={AuthType.Email} onChange={onChange} />
          </div>

          <div className="px-6 mt-5">
            {LoginViewRender}
          </div>

          <div className="px-6 mt-5 flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className='flex-shrink-0'>
                <Checkbox
                  shape='square'
                  checkedColor={"var(--primary-color)"}
                  checked={isAgreed}
                  onChange={handleAgreementToggle}
                />
              </div>
              <span className="text-sm text-gray-600">{t('我同意')}</span>
              <span className="text-sm text-gray-600 underline">
                {t('隐私政策')}
              </span>
            </div>
            <Link to="/unable-login" className="text-sm text-[var(--primary-color)] text-right">
              {t('无法登录问号')}
            </Link>
          </div>

          <div className="px-6 mt-5">
            <CustomButton
              onClick={handleLogin}
              loading={loading}
              disabled={!isAgreed || !isFormValid || loading}
            >
              {t('开始使用')}
            </CustomButton>
          </div>

          <div className="px-6 mt-6 flex items-center justify-center space-x-2">
            <span className="text-sm text-gray-600">{t('WPAY新用户')}</span>
            <Link to="/register" className="text-sm text-[var(--primary-color)]">
              {t('注册')}
            </Link>
          </div>
        </div>

        {/* Other login options */}
        {/* <div className="px-6 mt-9 pb-8">
          <OtherLoginItems />
        </div> */}
      </div>
    </div>
  )
})